import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Search, X, Loader2, Filter, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { notesApi } from '../../lib/notesApi';
import { getCurrentUser } from '../../lib/auth';
import { NoteItem } from './NoteItem';
import { NoteComposer } from './NoteComposer';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Everything' },
  { value: 'notes', label: 'Notes only' },
  { value: 'tasks', label: 'All tasks' },
];

const STATE_OPTIONS = [
  { value: '', label: 'Any state' },
  { value: 'todo', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'done', label: 'Done' },
];

export const NotesDrawer = (props) => {
  const { open, onOpenChange, accountId, pageKey, pageLabel, onCountsChange } = props;
  const me = useMemo(() => getCurrentUser(), []);
  const [notes, setNotes] = useState([]);
  const [counts, setCounts] = useState({ total: 0, open_tasks: 0, important: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState('all');
  const [taskState, setTaskState] = useState('');
  const [authorFilter, setAuthorFilter] = useState('all');
  const [taggedFilter, setTaggedFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [importantOnly, setImportantOnly] = useState(false);

  const reload = useCallback(
    async (focusNoteId) => {
      if (!open || !accountId || !pageKey) return;
      setLoading(true);
      try {
        const params = {
          account_id: accountId,
          page_key: pageKey,
          limit: 200,
        };
        if (search) params.search = search;
        if (status !== 'all') params.status = status;
        if (taskState) params.task_state = taskState;
        if (authorFilter === 'me' && me) params.author_id = me.id;
        if (taggedFilter === 'me' && me) params.tagged_user_id = me.id;
        if (assigneeFilter === 'me' && me) params.assignee_id = me.id;
        if (importantOnly) params.is_important = true;

        const data = await notesApi.list(params);
        setNotes((data && data.notes) || []);
        setCounts((data && data.counts) || { total: 0, open_tasks: 0, important: 0 });
        if (onCountsChange) onCountsChange();
        if (focusNoteId) {
          requestAnimationFrame(() => {
            const el = document.querySelector('[data-note-id="' + focusNoteId + '"]');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
      } catch (e) {
        toast.error((e && e.response && e.response.data && e.response.data.detail) || 'Could not load notes');
      } finally {
        setLoading(false);
      }
    },
    [open, accountId, pageKey, search, status, taskState, authorFilter, taggedFilter, assigneeFilter, importantOnly, me, onCountsChange]
  );

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const noteId = params.get('note_id');
    if (noteId) reload(noteId);
  }, [open, reload]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleCreate = async (body) => {
    await notesApi.create(body);
    toast.success(body.is_task ? 'Task posted' : 'Note added');
    reload();
  };

  const repliesProvider = useCallback(
    async (parentId) => {
      const data = await notesApi.list({
        account_id: accountId,
        page_key: pageKey,
        parent_id: parentId,
        limit: 200,
      });
      return (data && data.notes) || [];
    },
    [accountId, pageKey]
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="p-0 sm:max-w-[50vw] w-full flex flex-col"
        data-testid="notes-drawer"
        hideCloseButton
      >
        <VisuallyHidden.Root>
          <SheetTitle>Notes for {pageLabel}</SheetTitle>
          <SheetDescription>
            View, add, edit, or delete notes and tasks scoped to the {pageLabel} page.
          </SheetDescription>
        </VisuallyHidden.Root>

        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">
              Notes &middot; <span className="text-slate-500 font-normal">{pageLabel}</span>
            </div>
            <div className="text-[11px] text-slate-400">
              {counts.total} notes &middot; {counts.open_tasks} open tasks
              {counts.important > 0 && (
                <span className="ml-1 inline-flex items-center gap-0.5 text-amber-600">
                  &middot; <Flag className="h-2.5 w-2.5" fill="currentColor" /> {counts.important} important
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            data-testid="notes-drawer-close"
            onClick={() => onOpenChange(false)}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 space-y-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search notes, people, content..."
              data-testid="notes-search-input"
              className="w-full rounded-md border border-slate-200 bg-white pl-7 pr-2 py-1.5 text-xs placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500">
              <Filter className="h-3 w-3" /> Filter
            </span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="notes-status-filter"
              className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-700 focus:border-teal-500 focus:outline-none"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {status === 'tasks' && (
              <select
                value={taskState}
                onChange={(e) => setTaskState(e.target.value)}
                data-testid="notes-task-state-filter"
                className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-700 focus:border-teal-500 focus:outline-none"
              >
                {STATE_OPTIONS.map((o) => (
                  <option key={o.value || 'any'} value={o.value}>{o.label}</option>
                ))}
              </select>
            )}
            <select
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
              data-testid="notes-author-filter"
              className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-700 focus:border-teal-500 focus:outline-none"
            >
              <option value="all">By anyone</option>
              <option value="me">By me</option>
            </select>
            <select
              value={taggedFilter}
              onChange={(e) => setTaggedFilter(e.target.value)}
              data-testid="notes-tagged-filter"
              className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-700 focus:border-teal-500 focus:outline-none"
            >
              <option value="all">Tagging anyone</option>
              <option value="me">Tagging me</option>
            </select>
            <select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              data-testid="notes-assignee-filter"
              className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-700 focus:border-teal-500 focus:outline-none"
            >
              <option value="all">Assigned to anyone</option>
              <option value="me">Assigned to me</option>
            </select>
            <button
              type="button"
              onClick={() => setImportantOnly((v) => !v)}
              data-testid="notes-important-filter"
              aria-pressed={importantOnly}
              className={
                'inline-flex items-center gap-1 rounded-md border px-1.5 py-1 text-[11px] ' +
                (importantOnly
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-amber-50')
              }
            >
              <Flag className="h-3 w-3" fill={importantOnly ? 'currentColor' : 'none'} />
              Important
            </button>
          </div>
        </div>

        <div data-testid="notes-list" className="flex-1 overflow-y-auto px-3 py-2">
          {loading && (
            <div className="flex items-center justify-center py-8 text-xs text-slate-400">
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Loading...
            </div>
          )}
          {!loading && notes.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-200 bg-slate-50/50 py-10 text-center text-xs text-slate-500">
              No notes match the filters. Be the first to add one for <strong>{pageLabel}</strong>.
            </div>
          )}
          {!loading && notes.map((n) => (
            <div key={n.id} data-note-id={n.id}>
              <NoteItem
                note={n}
                currentUser={me}
                accountId={accountId}
                onChanged={reload}
                repliesProvider={repliesProvider}
              />
            </div>
          ))}
        </div>

        <NoteComposer
          accountId={accountId}
          pageKey={pageKey}
          pageLabel={pageLabel}
          onSubmit={handleCreate}
        />
      </SheetContent>
    </Sheet>
  );
};

export default NotesDrawer;
